<script>
import { computed } from 'vue-demi';
import moment from 'moment';
import { isString } from 'lodash';
import { useRecentsLoader } from '@/api';
import BlankStateImage from '@/assets/images/blankslates/abstract.svg';
import BlankState from '@/platform/components/common/BlankState';
import ListItem from '@/platform/components/side-nav/panels/search/common/ListItem.vue';
import SkeletonListItem from '@/platform/components/side-nav/panels/search/common/SkeletonListItem.vue';
import usePreventLoaderFlash from '@/platform/composables/usePreventLoaderFlash';

export default {
  name: 'Recents',
  components: {
    BlankState,
    BlankStateImage,
    ListItem,
    SkeletonListItem,
  },
  setup() {
    const loader = useRecentsLoader({
      params: {},
      count: 50,
      pageSize: 50,
      useElastic: true,
    });

    const { response, items, error, loading } = loader;

    const initialized = computed(() => response.value !== undefined);

    const { shouldShowLoader: shouldShowLoadingState } = usePreventLoaderFlash(
      computed(() => !initialized.value || loading.value),
    );

    const skeletonCount = computed(() => 20);

    const shouldShowEmptyState = computed(() => {
      return initialized.value && !loading.value && ((!error.value && !items.value.length) || error.value);
    });

    const actionTypesTextMap = Object.freeze({
      viewed: 'viewed',
      new: 'added',
      edited: 'updated',
      deleted: 'deleted',
      completed: 'completed',
      reopened: 'reopened',
      reacted: 'reacted',
    });

    const getEntityGrouping = (recent) => {
      if (recent.entityType.includes('invoice')) {
        return 'invoice';
      }

      if (recent.entityType.includes('comment')) {
        return 'comment';
      }

      return recent.entityType;
    };

    const getEntityWording = (entity) => {
      let wording = '';

      switch (entity) {
        case 'billinginvoice':
          wording = 'an invoice';
          break;
        case 'task_comment':
        case 'milestone_comment':
        case 'notebook_comment':
        case 'link_comment':
        case 'file_comment':
          wording = `a ${entity.split('_').join(' ')}`;
          break;
        default:
          wording = `a ${entity}`;
      }

      return wording;
    };

    function buildActionTypeText(recent) {
      const action = actionTypesTextMap[recent.actionType];

      // Note: Special case for reactions, should read 'reacted to'
      if (action === 'reacted') {
        return `You ${action} to ${getEntityWording(recent.entityType)}`;
      }

      return `You ${action} ${getEntityWording(recent.entityType)}`;
    }

    function buildDescriptionText(recent) {
      const description = (isString(recent.description) && recent.description) || '';

      if (/^\s*$/.test(description)) {
        return `View ${getEntityGrouping(recent)}`;
      }

      return description;
    }

    function getParsedDate(date) {
      return moment(date).fromNow();
    }

    return {
      buildActionTypeText,
      buildDescriptionText,
      shouldShowEmptyState,
      shouldShowLoadingState,
      recents: items,
      loading,
      error,
      skeletonCount,
      getParsedDate,
    };
  },
};
</script>

<template>
  <div class="select-none">
    <div class="flex w-full h-full relative select-none">
      <Transition name="fade">
        <template v-if="shouldShowEmptyState">
          <div class="flex-1 min-h-0 pb-16 overflow-y-auto scrollbar">
            <BlankState class="min-h-[275px] h-full">
              <template #illustration>
                <BlankStateImage class="w-[120px]" />
              </template>
              <template #heading>
                {{ $t('No recent activity') }}
              </template>
              <template #line-1>
                {{ $t("You don't have any recent activity") }}
              </template>
            </BlankState>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col w-full h-full">
            <div class="flex-none flex items-center mb-3 px-6">
              <!-- icon-recents -->
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 10 10"
                width="16"
                height="16"
                class="text-text-tertiary fill-current mr-3"
              >
                <path
                  d="m4.9485392 2.19821552-.24143546-.00185243c-.06360065 0-.12534885.02469896-.17042503.06977457-.04569367.0450756-.07162791.10620552-.07162791.16980535l-.02037691 3.22444922c0 .03210865.00617482.06359983.01790698.0926211.01234964.02963876.02963913.05619014.05248597.07903668.02222935.02222906.04878108.0401358.07842021.05248529.02902166.01234948.06051324.01852422.0926223.01852422l2.38718541-.00679222c.06360065 0 .12534885-.0259339.17042504-.0710095.04507618-.04507561.07039294-.10620554.07039294-.17042283v-.24143234c0-.06359982-.02531676-.12534722-.07039294-.17042282-.04507619-.0450756-.10682439-.07039204-.17042504-.07039204l-1.9006096.00555727.01605454-2.73726224c.00061748-.03210865-.00555734-.06359982-.01790698-.0926211-.01173216-.02963875-.02963913-.05619014-.05186849-.07903667-.02222935-.02222907-.04878107-.04013581-.07780273-.0524853-.02963913-.01234947-.06113072-.01852421-.0926223-.01852421Z"
                />
                <path
                  d="M8.53857955 1.47947578C8.07546805 1.01204796 7.52467411.64032861 6.9176893.3865468 6.3107045.13276499 5.65987847.00124302 5.00164265.00000808c-.65761833-.00123495-1.30967932.12719964-1.9172816.37851156C2.47614126.62983156 1.92411236.999081 1.45914841 1.4646564.99418447 1.9302318.6261652 2.4834885.37546751 3.0917004.1253873 3.69991227-.00243147 4.35196482.00003845 5.00957463c.00246993.65822728.13461108 1.30904488.38963115 1.91540435.25502006.60635946.62674423 1.1565288 1.09541306 1.61901682L.736077 8.53164633H.73051966c-.08521252-.00061747-.16733762.03272613-.22785086.0926211-.06113072.05989498-.0957097.14140155-.09632719.22661296-.00061748.08521141.03272655.16733546.0926223.2278479.05989575.06112993.14140338.09570848.2266159.09632595l1.59742593.02593391h.00617482c.08459503 0 .16548517-.0333436.22538093-.09200363.06051323-.0592775.09447474-.1401666.09632719-.22414306l.02593424-1.59802271c.00123497-.08521141-.0308741-.16733545-.09015237-.22846538-.05927827-.0617474-.1407859-.09694342-.22599841-.09817837H2.3551148c-.08459503 0-.16548517.0333436-.22538093.09200363-.06051323.0592775-.09447474.1401666-.09632719.22414306l-.01420209.88916256C1.28440101 7.47020853.8151147 6.54091015.69223578 5.53689743c-.12287892-1.00401272.10744186-2.01913998.65206099-2.8712541C1.88891589 1.81352921 2.71325437 1.177531 3.6759088.86755905 4.63856324.5575871 5.67963789.59216565 6.61882801.96697236c.9398076.37418924 1.71968737 1.0639077 2.2056457 1.95121784.48534086.88669266.64712114 1.91540435.4563192 2.90892001-.19018445.9928982-.72121897 1.88885297-1.50048126 2.53287835-.77987976.64464286-1.7598237.99660304-2.77125921.99660304-.08521252 0-.16672014.03396107-.22723338.09447352-.06051323.06051245-.09385726.14201902-.09385726.22723043 0 .08521141.03334403.16733546.09385726.2278479.06051324.05989499.14202086.09385606.22723338.09385606.98735372 0 1.95247808-.29330015 2.77311166-.84161707.82063358-.5483169 1.46034493-1.32818657 1.83824391-2.24019567.3772815-.91139162.47607863-1.91540435.28404172-2.88360358-.19265438-.96819923-.66811552-1.85736179-1.36587018-2.55510741Z"
                />
              </svg>
              <h4 class="flex-1 text-default font-bold m-0 text-text">
                {{ $t('My recent activity') }}
              </h4>
            </div>
            <ul
              class="list-none text-xs p-0 m-0 px-3"
              :class="[...(!shouldShowLoadingState ? ['overflow-y-auto', 'scrollbar'] : ['overflow-hidden'])]"
            >
              <template v-if="shouldShowLoadingState">
                <SkeletonListItem v-for="index in skeletonCount" :key="index" />
              </template>
              <template v-else>
                <template v-for="recent in recents">
                  <ListItem
                    :id="`${recent.entityType}-${recent.entityId}`"
                    :key="`${recent.entityType}-${recent.entityId}`"
                    :type="recent.entityType"
                    :link="recent.itemLink"
                    :title="$t(buildDescriptionText(recent))"
                    identifierPrefix="app-nav__search-panel__recents"
                    @click="$emit('item-clicked', recent, $event)"
                  >
                    <template #line-1-left>
                      {{ $t(buildActionTypeText(recent)) }}
                    </template>
                    <template #line-1-right>
                      {{ getParsedDate(recent.createdAt) }}
                    </template>
                    <template #line-2>
                      {{ $t(buildDescriptionText(recent)) }}
                    </template>
                  </ListItem>
                </template>
              </template>
            </ul>
          </div>
        </template>
      </Transition>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '~@tko/src/styles/mixins/mixins';

.scrollbar {
  @include scrollbar();
}

.fade-enter-active,
.fade-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
